import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { Field } from '@/components/Field'
import { Chip } from '@/components/Chip'
import { GridTable } from '@/components/GridTable'
import { WizardProse, WizardButtons, WizardSplit, WizardCard, WizardHeading } from '@/components/WizardParts'
import { DestinationWorkspacePicker } from './DestinationWorkspacePicker'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig } from '@/layers/restore-wizard/types'

type SnapshotPullRequest = components['schemas']['SnapshotPullRequest']
type ProbePresence = components['schemas']['ProbePresence']

const PRESENCE_LABEL: Record<string, string> = { PRESENT: 'Present', ABSENT: 'Absent', NOT_CHECKED: 'Not checked' }
const PRESENCE_CHIP: Record<string, 'warning' | 'active' | 'pending'> = { PRESENT: 'warning', ABSENT: 'active', NOT_CHECKED: 'pending' }

/**
 * `review-wz-2` — `REVIEW_STATE`'s `destination` pane. **`RestoreInput` has no field narrower than
 * `repository_ids`/`object_types` for "which pull requests"** — confirmed against the contract,
 * not assumed — so this pane lists the snapshot's captured pull requests and probes each one's
 * branch pair for advisory presence (`probePullRequestBranches`), but the restore itself recreates
 * every captured pull request for the chosen repositories; nothing here narrows a request field.
 * Reported as a gap. **Only the first selected repository** is listed/probed this pass — a
 * multi-repository review pane is a real simplification, also reported.
 *
 * **Three real fixes added W162.** `jobListSnapshotPullRequests` is cursor-paginated on the server
 * and had no `cursor` wired at all — silent truncation on any repository with more captured pull
 * requests than one page. **The spec's own `author` column had never been rendered at all** —
 * `SnapshotPullRequest.author` is a real, required field; added as its own column. `state_captured_at`
 * is a `MachineTime` with no served `ScreenTime` twin — was rendered raw, now reads through
 * `formatJobTime()`, the established narrow fallback for exactly this class of field.
 *
 * **Both presence chips recoloured (W172)** — see `TargetWorkspaceHooksPane.tsx`'s own doc comment
 * for the same fix and the same evidence (`ref-wz-2`'s own `Absent` → `st-active`); was flat grey
 * for every state.
 */
export function TargetPullRequestsPane({
  config,
  heading,
  jobId,
  repositoryId,
  destinationWorkspaceName,
  onNext,
  onBack,
}: {
  config: RestoreWizardConfig
  heading: string
  jobId: string
  repositoryId: string
  destinationWorkspaceName: string
  onNext: (args: { destinationWorkspaceId?: string; destinationLabel: string }) => void
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const [destination, setDestination] = useState<{ id: string; name: string }>({ id: workspaceId, name: destinationWorkspaceName })
  const pullRequestsPager = useCursorPager()

  const pullRequests = useQuery({
    queryKey: ['snapshot-pull-requests', orgId, workspaceId, jobId, repositoryId, pullRequestsPager.cursor],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/repositories/{repo_id}/pull-requests', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId, repo_id: repositoryId }, query: { cursor: pullRequestsPager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })
  const pullRequestRows = (pullRequests.data?.items ?? []) as unknown as readonly SnapshotPullRequest[]

  const branches = [...new Set(pullRequestRows.flatMap((pr) => [pr.source_branch, pr.destination_branch]))]

  const presence = useQuery({
    queryKey: ['pr-branch-presence', orgId, workspaceId, repositoryId, branches],
    enabled: branches.length > 0,
    queryFn: async (): Promise<ProbePresence> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/probes/pull-request-branches', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { repository_id: repositoryId, branch: branches } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ProbePresence
    },
  })

  const problem = pullRequests.error instanceof ApiProblemError ? pullRequests.error.problem : undefined

  return (
    <div data-scr={brandUnitId(config.unitIds.targets)} data-cmp="pane">
      <WizardSplit>
        <div>
          <WizardHeading>{heading}</WizardHeading>
          <WizardProse>Every captured pull request for the chosen repository is recreated with its title, description and branch pair — comment threads stay on the original.</WizardProse>
        </div>

        <WizardCard>
          <h4 className="m-0 mb-4 text-body-lg font-semibold">Destination and scope</h4>

          <Field label="Destination">
            <DestinationWorkspacePicker currentWorkspaceId={workspaceId} value={destination.id} onChange={(id, name) => setDestination({ id, name })} />
          </Field>

          <WizardButtons>
            <Button data-act={actionId(`nav.${config.unitIds.select}`)} ghost onClick={onBack}>
              Back
            </Button>
            <Button
              data-act={actionId(`nav.${config.unitIds.plan}`)}
              onClick={() => onNext({ destinationWorkspaceId: destination.id !== workspaceId ? destination.id : undefined, destinationLabel: destination.name })}
            >
              Next
            </Button>
          </WizardButtons>
        </WizardCard>
      </WizardSplit>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
        <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
          <span className="flex-1">Pull requests</span>
          <div data-cmp="grid-tools">
            <IconButton
              data-act={undefined}
              icon="i-refresh"
              label="Refresh"
              disabled={pullRequests.isFetching}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['snapshot-pull-requests', orgId, workspaceId, jobId, repositoryId] })}
            />
          </div>
        </div>
        <GridTable
          columns={[
            { key: 'number', header: '#', render: (pr) => <span className="font-mono">#{pr.number}</span> },
            { key: 'title', header: 'Title', render: (pr) => pr.title },
            { key: 'author', header: 'Author', render: (pr) => pr.author },
            {
              key: 'branches',
              header: 'Branches',
              render: (pr) => {
                const source = presence.data?.items.find((i) => i.key === pr.source_branch)
                const destination = presence.data?.items.find((i) => i.key === pr.destination_branch)
                return (
                  <>
                    <Chip variant={source ? (PRESENCE_CHIP[source.presence] ?? 'plain') : 'plain'}>{pr.source_branch}: {source ? PRESENCE_LABEL[source.presence] : '…'}</Chip>{' '}
                    <Chip variant={destination ? (PRESENCE_CHIP[destination.presence] ?? 'plain') : 'plain'}>{pr.destination_branch}: {destination ? PRESENCE_LABEL[destination.presence] : '…'}</Chip>
                  </>
                )
              },
            },
            { key: 'captured', header: 'Captured', render: (pr) => formatJobTime(pr.state_captured_at) },
          ]}
          rows={pullRequestRows}
          getRowKey={(pr) => String(pr.number)}
          emptyMessage={pullRequests.isLoading ? '' : 'Nothing in this snapshot matches.'}
        />
        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
          <Note>{presence.data?.advisory_note ?? 'Each object is re-read as it is written; the report is what is true.'}</Note>
          <Pager
            hasPrev={pullRequestsPager.hasPrev}
            hasNext={Boolean(pullRequests.data?.next_cursor)}
            loading={pullRequests.isFetching}
            onPrev={pullRequestsPager.goPrev}
            onNext={() => {
              if (pullRequests.data?.next_cursor) pullRequestsPager.goNext(pullRequests.data.next_cursor)
            }}
          />
        </div>
      </div>
    </div>
  )
}
